import { useEffect, useMemo, useState } from 'react'

import type { ProductDataVariant } from '../../../../services/product/product-data'
import AutoDeliveryConfigService, {
  AutoDeliveryConfigData
} from '../../../../services/auto-delivery'
import { getAssetImageUrl } from '../../../../utils/asset-url'
import {
  AUTODELIVERY_ORDER_TYPEID,
  AUTODELIVERY_RECCURENT_TYPEID,
  CORESITE_LOCALSTORAGE
} from '../../../../utils/constants'
import { formatPrice, FormatPriceOptions } from '../../../../utils/format-price'
import { getOptimizedImageExtension } from '../../../../utils/image-extensions'
import type { MuleSoftConfigType } from '../../../../utils/page-config'
import type { CartProductEntry } from '../../common/cart-management/cartConfig'
import type { CartLocalStorageJSON } from '../../common/cart-management/types'
import type { AutoDeliveryConfig } from '../../common/config'
import { fetchContainerContent } from '../../common/dialog-configuration'
import type { GroupedItem, ProductListConfig } from '../models'
import type { ProductImageMap } from '../../../../services/auto-delivery/product-image-cache'
import { EMPTY_IMAGE } from '../utils/constants'
import {
  computeCategoryQuantities,
  getValidationMessage
} from '../utils/auto-delivery-validation'

interface StickyBarItem {
  id: string
  image: string
  imageAlt: string
  title: string
  quantity: number
  price: string
}

interface UseAutoDeliveryParams {
  isAutoDeliveryEnabled: boolean
  autoDeliveryConfig: AutoDeliveryConfig | undefined
  config: ProductListConfig['config']
  cartLocalStorageJSON: CartLocalStorageJSON | null | undefined
  data:
    { items: GroupedItem[]; facets: unknown[]; gcodes: string[] } | undefined
  isUserLoggedIn: boolean
  uid: string | undefined
  jwt: string | undefined
  setIsButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>
  pageLocale: string
  caConfig: { muleSoftConfig?: MuleSoftConfigType; market?: string }
  general: ProductListConfig['general']
  setIsAutoDeliveryUneligibilityDialogOpen: React.Dispatch<
    React.SetStateAction<boolean>
  >
  setAutoDeliveryUneligibilityDialogContent: React.Dispatch<
    React.SetStateAction<Record<string, unknown>>
  >
  productCount: number
  productImageCache: ProductImageMap
  formatPriceOptions?: FormatPriceOptions
}

interface UseAutoDeliveryReturn {
  autoDeliveryConfigData: AutoDeliveryConfigData | null
  resolvedOrderType: string
  isAutoDeliveryCartOrderType: boolean
  isAutoDeliveryMaximumReached: boolean
  disabledCategoryIds: string[]
  autoDeliveryCartEntries: CartProductEntry[]
  variantByCode: Map<string, ProductDataVariant>
  categoryQuantities: Map<string, number>
  stickyBarItems: StickyBarItem[]
  stickyItemsCount: number
  stickyBarTitle: string
  stickyBarTotalPrice: string
  stickyBarTotalValueLabel: string
  stickyBarValidation: {
    message: string
    continueDisabled: boolean
    disabledCategoryIds: string[]
  }
  productEntry: CartProductEntry | undefined
  miniCartTotalUnitCount: number
  cartLocalStorageDataForAddToCart: CartLocalStorageJSON | null | undefined
  zeroPriceFormatted: string
}

export const useAutoDelivery = (
  params: UseAutoDeliveryParams
): UseAutoDeliveryReturn => {
  const {
    isAutoDeliveryEnabled,
    autoDeliveryConfig,
    config,
    cartLocalStorageJSON,
    data,
    isUserLoggedIn,
    uid,
    jwt,
    setIsButtonDisabled,
    pageLocale,
    caConfig,
    general,
    setIsAutoDeliveryUneligibilityDialogOpen,
    setAutoDeliveryUneligibilityDialogContent,
    productCount,
    productImageCache,
    formatPriceOptions
  } = params

  const [autoDeliveryConfigData, setAutoDeliveryConfigData] =
    useState<AutoDeliveryConfigData | null>(null)

  const resolvedOrderType = useMemo(() => {
    if (!isAutoDeliveryEnabled) {
      return general?.orderType || ''
    }

    const isRecurringOrder =
      Boolean(cartLocalStorageJSON?.adChangeProduct) ||
      cartLocalStorageJSON?.cart?.orderType?.typeId ===
        AUTODELIVERY_RECCURENT_TYPEID

    return isRecurringOrder
      ? AUTODELIVERY_RECCURENT_TYPEID
      : AUTODELIVERY_ORDER_TYPEID
  }, [
    isAutoDeliveryEnabled,
    cartLocalStorageJSON?.adChangeProduct,
    cartLocalStorageJSON?.cart?.orderType?.typeId,
    general?.orderType
  ])

  const isAutoDeliveryCartOrderType = useMemo(() => {
    const cartOrderTypeId = cartLocalStorageJSON?.cart?.orderType?.typeId
    return (
      cartOrderTypeId === AUTODELIVERY_ORDER_TYPEID ||
      cartOrderTypeId === AUTODELIVERY_RECCURENT_TYPEID
    )
  }, [cartLocalStorageJSON?.cart?.orderType?.typeId])

  const shouldUseAutoDeliveryCartData =
    isAutoDeliveryEnabled && isAutoDeliveryCartOrderType

  const shouldUseMiniCartCartData =
    !isAutoDeliveryEnabled && !isAutoDeliveryCartOrderType

  const productEntry = shouldUseMiniCartCartData
    ? cartLocalStorageJSON?.entry
    : undefined

  const autoDeliveryCartEntries = useMemo(
    () =>
      shouldUseAutoDeliveryCartData
        ? (cartLocalStorageJSON?.cart?.entries ?? [])
        : [],
    [shouldUseAutoDeliveryCartData, cartLocalStorageJSON?.cart?.entries]
  )

  const variantByCode = useMemo(() => {
    const map = new Map<string, ProductDataVariant>()
    data?.items?.forEach((item: GroupedItem) => {
      item?.variants?.forEach((variant: ProductDataVariant) => {
        if (variant?.globalArticleCode) {
          map.set(variant.globalArticleCode, variant)
        }
      })
    })
    return map
  }, [data?.items])

  const categoryQuantities = useMemo(
    () => computeCategoryQuantities(autoDeliveryCartEntries, variantByCode),
    [autoDeliveryCartEntries, variantByCode]
  )

  const autoDeliveryPeriodicityCategories = useMemo(() => {
    if (!autoDeliveryConfigData?.periodicities?.length) {
      return []
    }
    const { defaultPeriodicity } = autoDeliveryConfigData
    const match = autoDeliveryConfigData.periodicities.find(
      (p) => p.periodicity === defaultPeriodicity
    )
    return match?.categories ?? []
  }, [autoDeliveryConfigData])

  const stickyBarItems = useMemo((): StickyBarItem[] => {
    if (!isAutoDeliveryEnabled) {
      return []
    }

    const entries = autoDeliveryCartEntries

    return entries
      .map((entry: CartProductEntry, index: number) => {
        const code = entry?.product?.code
        const variant = code ? variantByCode.get(code) : undefined
        const assetUrl = getAssetImageUrl(variant?.assets?.[0])
        const cachedUrl =
          !assetUrl && code ? productImageCache[code] : undefined
        const resolvedUrl = assetUrl ?? cachedUrl
        const imageSrc = resolvedUrl
          ? `${getOptimizedImageExtension(resolvedUrl)}?width=64`
          : EMPTY_IMAGE
        const title = variant?.globalArticleName ?? entry?.product?.name ?? ''
        const price =
          entry?.totalPrice?.formattedValue ??
          entry?.totalPriceWithoutDiscounts?.formattedValue ??
          entry?.totalWithoutDiscounts?.formattedValue ??
          ''

        return {
          id: code ?? `${index}`,
          image: imageSrc,
          imageAlt: title,
          title,
          quantity: Number(entry?.quantity ?? 0),
          price
        }
      })
      .filter((item: StickyBarItem) => item.quantity > 0)
  }, [
    isAutoDeliveryEnabled,
    autoDeliveryCartEntries,
    variantByCode,
    productImageCache
  ])

  const stickyItemsCount = useMemo(() => {
    const totalUnitCount = shouldUseAutoDeliveryCartData
      ? cartLocalStorageJSON?.cart?.totalUnitCount
      : 0
    const parsedCount =
      typeof totalUnitCount === 'number'
        ? totalUnitCount
        : Number(totalUnitCount ?? 0)
    if (!Number.isNaN(parsedCount) && parsedCount > 0) {
      return parsedCount
    }
    return stickyBarItems.reduce(
      (sum: number, item: StickyBarItem) => sum + item.quantity,
      0
    )
  }, [
    shouldUseAutoDeliveryCartData,
    cartLocalStorageJSON?.cart?.totalUnitCount,
    stickyBarItems
  ])

  const autoDeliveryMaxCart = useMemo(() => {
    if (!autoDeliveryConfigData?.periodicities?.length) {
      return undefined
    }
    const { defaultPeriodicity } = autoDeliveryConfigData
    const match = autoDeliveryConfigData.periodicities.find(
      (p) => p.periodicity === defaultPeriodicity
    )
    return match?.maxCart
  }, [autoDeliveryConfigData])

  const autoDeliveryMinCart = 1

  const isAutoDeliveryMaximumReached = useMemo(
    () =>
      Boolean(
        isAutoDeliveryEnabled &&
        autoDeliveryMaxCart &&
        stickyItemsCount >= autoDeliveryMaxCart
      ),
    [isAutoDeliveryEnabled, autoDeliveryMaxCart, stickyItemsCount]
  )

  const stickyBarValidation = useMemo(() => {
    return getValidationMessage({
      stickyItemsCount,
      isAutoDeliveryCartOrderType,
      autoDeliveryMaxCart: autoDeliveryMaxCart ?? 0,
      autoDeliveryMinCart,
      autoDeliveryConfig: autoDeliveryConfig ?? {},
      categoryQuantities,
      periodicityCategories: autoDeliveryPeriodicityCategories,
      categoryNames: autoDeliveryConfig?.categoryNames ?? []
    })
  }, [
    stickyItemsCount,
    isAutoDeliveryCartOrderType,
    autoDeliveryMaxCart,
    autoDeliveryConfig,
    categoryQuantities,
    autoDeliveryPeriodicityCategories
  ])

  const { disabledCategoryIds } = stickyBarValidation

  const stickyBarTitle = useMemo(() => {
    const label =
      autoDeliveryConfig?.stickyBarLabel ?? autoDeliveryConfig?.itemsLabel
    if (!label) {
      return ''
    }
    return label
      .replace('$items_total', String(stickyItemsCount))
      .replace(/\s{2,}/g, ' ')
      .trim()
  }, [
    autoDeliveryConfig?.stickyBarLabel,
    autoDeliveryConfig?.itemsLabel,
    stickyItemsCount
  ])

  const zeroPriceFormatted = useMemo(() => {
    const currency = data?.items?.[0]?.variants?.[0]?.price?.priceCurrency

    return (
      formatPrice({
        value: 0,
        currency,
        locale: pageLocale,
        options: formatPriceOptions
      }) ?? ''
    )
  }, [data?.items, pageLocale, formatPriceOptions])

  const stickyBarTotalPrice = shouldUseAutoDeliveryCartData
    ? (cartLocalStorageJSON?.cart?.totalPrice?.formattedValue ??
      zeroPriceFormatted)
    : zeroPriceFormatted

  const miniCartTotalUnitCount = shouldUseMiniCartCartData
    ? Number(cartLocalStorageJSON?.cart?.totalUnitCount ?? 0)
    : 0

  const cartLocalStorageDataForAddToCart =
    !isAutoDeliveryEnabled && isAutoDeliveryCartOrderType
      ? undefined
      : cartLocalStorageJSON

  const stickyBarTotalValueLabel = useMemo(() => {
    if (!autoDeliveryConfig?.totalValueLabel) {
      return stickyBarTotalPrice
    }
    return autoDeliveryConfig.totalValueLabel.replace(
      '$total_value',
      stickyBarTotalPrice
    )
  }, [autoDeliveryConfig?.totalValueLabel, stickyBarTotalPrice])

  // Auto-delivery config fetch effect
  useEffect(() => {
    if (!isAutoDeliveryEnabled) {
      return
    }

    const muleSoftConfig = caConfig?.muleSoftConfig as MuleSoftConfigType
    if (!muleSoftConfig?.url) {
      return
    }

    const consumerId = uid ? atob(uid) : undefined
    const token = jwt || 'anonymous'

    const fetchADConfig = async () => {
      setIsButtonDisabled(true)
      try {
        const result = await AutoDeliveryConfigService.fetchAutoDeliveryConfig({
          config: config as unknown as Parameters<
            typeof AutoDeliveryConfigService.fetchAutoDeliveryConfig
          >[0]['config'],
          token,
          ...(consumerId && { consumerId })
        })

        if (result && result?.status === 200) {
          setAutoDeliveryConfigData(result)

          // DCXWAM-39033: Read cart directly from localStorage to avoid stale closure
          // caused by SSR hydration timing (resolvedOrderType may still be 'ZAD' when
          // this async callback executes on first mount). Do NOT replace with hook state.
          const currentCartRaw = window.localStorage?.getItem(
            CORESITE_LOCALSTORAGE
          )
          const currentCartData = currentCartRaw
            ? JSON.parse(currentCartRaw)
            : null
          const isEditFlow =
            currentCartData?.cart?.orderType?.typeId ===
            AUTODELIVERY_RECCURENT_TYPEID

          if (
            isUserLoggedIn &&
            consumerId &&
            !result?.canCreateAD &&
            !isEditFlow
          ) {
            setIsAutoDeliveryUneligibilityDialogOpen(true)
            fetchContainerContent(
              autoDeliveryConfig?.alreadySubscribedDialogContent,
              setAutoDeliveryUneligibilityDialogContent
            )
          }
        }
      } catch {
        setAutoDeliveryConfigData(null)
      } finally {
        setIsButtonDisabled(false)
      }
    }
    fetchADConfig()
  }, [isAutoDeliveryEnabled, isUserLoggedIn, uid, jwt])

  // For auto delivery sticky bar calculating the position correctly on page load
  useEffect(() => {
    if (!isAutoDeliveryEnabled) {
      return () => {}
    }

    let frame1 = 0
    let frame2 = 0

    frame1 = window.requestAnimationFrame(() => {
      frame2 = window.requestAnimationFrame(() => {
        window.dispatchEvent(new Event('resize'))
      })
    })

    return () => {
      if (frame1) {
        window.cancelAnimationFrame(frame1)
      }

      if (frame2) {
        window.cancelAnimationFrame(frame2)
      }
    }
  }, [isAutoDeliveryEnabled, productCount])

  return {
    autoDeliveryConfigData,
    resolvedOrderType,
    isAutoDeliveryCartOrderType,
    isAutoDeliveryMaximumReached,
    disabledCategoryIds,
    autoDeliveryCartEntries,
    variantByCode,
    categoryQuantities,
    stickyBarItems,
    stickyItemsCount,
    stickyBarTitle,
    stickyBarTotalPrice,
    stickyBarTotalValueLabel,
    stickyBarValidation,
    productEntry,
    miniCartTotalUnitCount,
    cartLocalStorageDataForAddToCart,
    zeroPriceFormatted
  }
}
